import React, { useState, useEffect } from 'react';
import api from '../api/axios';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: []
  });

  const modules = ['broadcasts', 'goals', 'projects', 'assets', 'leaves', 'requests', 'employees', 'roles', 'profile', 'leads', 'sales', 'deals', 'products', 'payroll', 'attendance', 'history', 'nda', 'sales-reports', 'devices', 'biometric-attendance', 'documents', 'my-payslips', 'my-documents'];
  const actions = ['create', 'read', 'update', 'delete'];

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handlePermissionToggle = (module, action) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      const moduleIndex = permissions.findIndex(p => p.module === module);

      if (moduleIndex === -1) {
        // Module doesn't exist, create it with the action
        permissions.push({ module, actions: [action] });
      } else {
        // Module exists, toggle the action
        const actions = [...permissions[moduleIndex].actions];
        const actionIndex = actions.indexOf(action);

        if (actionIndex === -1) {
          // Action doesn't exist, add it
          actions.push(action);
          permissions[moduleIndex] = { ...permissions[moduleIndex], actions };
        } else {
          // Action exists, remove it
          actions.splice(actionIndex, 1);
          if (actions.length === 0) {
            // No actions left, remove the module
            permissions.splice(moduleIndex, 1);
          } else {
            permissions[moduleIndex] = { ...permissions[moduleIndex], actions };
          }
        }
      }

      return { ...prev, permissions };
    });
  };

  const isPermissionChecked = (module, action) => {
    const modulePermission = formData.permissions.find(p => p.module === module);
    return modulePermission && modulePermission.actions.includes(action);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/roles/${editingRoleId}`, formData);
      } else {
        await api.post('/roles', formData);
      }
      setShowModal(false);
      setEditMode(false);
      setEditingRoleId(null);
      setFormData({ name: '', permissions: [] });
      fetchRoles();
    } catch (error) {
      const errorMessage = error.response?.data?.message || (editMode ? 'Error updating role' : 'Error creating role');
      alert(errorMessage);
    }
  };

  const handleEdit = (role) => {
    setEditMode(true);
    setEditingRoleId(role._id);
    setFormData({
      name: role.name,
      permissions: role.permissions
    });
    setShowModal(true);
  };

  const handleDelete = async (roleId, isDefault) => {
    if (isDefault) {
      alert('Cannot delete default role');
      return;
    }

    if (window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      try {
        await api.delete(`/roles/${roleId}`);
        fetchRoles();
      } catch (error) {
        alert(error.response?.data?.message || 'Error deleting role');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingRoleId(null);
    setFormData({ name: '', permissions: [] });
  };

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Role Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Create Role
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Permissions</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role._id}>
                <td>{role.name}</td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {role.permissions.map((perm, idx) => (
                      <span key={idx} className="badge badge-info">
                        {perm.module}: {perm.actions.join(', ')}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  {role.isDefault && <span className="badge badge-warning">Default</span>}
                </td>
                <td>
                  <div className="flex" style={{ gap: '8px' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleEdit(role)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(role._id, role.isDefault)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                      disabled={role.isDefault}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{editMode ? 'Edit Role' : 'Create Role'}</h2>
              <button className="close-btn" onClick={handleCloseModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <table className="table" style={{ marginTop: '10px' }}>
                  <thead>
                    <tr>
                      <th>Module</th>
                      {actions.map(action => (
                        <th key={action}>{action.charAt(0).toUpperCase() + action.slice(1)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(module => (
                      <tr key={module}>
                        <td>{module.charAt(0).toUpperCase() + module.slice(1)}</td>
                        {actions.map(action => (
                          <td key={action}>
                            <input
                              type="checkbox"
                              checked={isPermissionChecked(module, action)}
                              onChange={() => handlePermissionToggle(module, action)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editMode ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roles;
